import { serviceJsonLd, SHARED_STEPS, SHARED_STATS } from "../types";
import type { LandingContent } from "../types";

const faqs = [
  {
    q: "Is the assessment really free? What's the catch?",
    a: "Yes, it's free, and there's no catch. The assessment is how we start serious engagements: you get a genuinely useful report whether or not we ever work together. If we're a good fit, you'll know exactly why — with numbers.",
  },
  {
    q: "What do you need from me for the assessment?",
    a: "Just 30 minutes on a video call and honest answers about how your team works today: where time goes, which tasks repeat, and which tools you use. No technical preparation needed.",
  },
  {
    q: "Is this just a disguised sales call?",
    a: "No. The session is a working session: we map your workflows live and identify automation opportunities together. You'll receive a written report within 24 hours — prioritized use cases, ROI estimates and a 90-day roadmap — yours to keep, no strings attached.",
  },
  {
    q: "What if AI turns out not to be a fit for my business?",
    a: "Then we'll tell you. About one in five assessments ends with our honest recommendation to wait or fix foundations first. We'd rather earn your trust than sell you something that won't pay off.",
  },
  {
    q: "Do you sign NDAs before the session?",
    a: "Absolutely. If you'd like, we'll sign a mutual NDA before you share anything sensitive about your operations.",
  },
  {
    q: "Who is the assessment for?",
    a: "Small and mid-size businesses — typically 15+ employees — where owners or executives feel the team losing time to repetitive, manual work. If that's you, the assessment will find the leverage points.",
  },
];

export const assessmentContent: LandingContent = {
  seo: {
    title: "Free AI Readiness Assessment | AIdeal Media",
    description:
      "Free AI readiness assessment for SMBs: discover your 3 highest-ROI automation opportunities, with cost estimates and a 90-day roadmap. Report in 24 hours.",
    keywords: "ai readiness assessment, free ai assessment, ai audit for small business, ai opportunity report",
    canonical: "https://www.aidealmedia.info/ai-readiness-assessment/",
    ogImage: "https://www.aidealmedia.info/img/hero-bg.jpg",
    jsonLd: serviceJsonLd({
      url: "https://www.aidealmedia.info/ai-readiness-assessment/",
      name: "Free AI Readiness Assessment",
      description:
        "Free AI readiness assessment for SMBs: top 3 highest-ROI automation opportunities, cost estimates and a 90-day roadmap, delivered in 24 hours.",
      faqs,
    }),
  },
  badge: "Free · No commitment · Written report in 24 hours",
  h1a: "Free ",
  h1gradient: "AI Readiness Assessment",
  h1b: "",
  sub: "In 30 minutes, we'll identify the 3 tasks in your business where AI pays for itself fastest — and send you a written report with ROI estimates and a 90-day roadmap.",
  problemTitle: "78% of companies are piloting AI. Only 12% capture ROI.",
  problemIntro:
    "The difference isn't the technology — it's knowing exactly where to apply it. Most businesses either automate the wrong things or freeze waiting for the 'perfect' use case. The assessment removes the guesswork.",
  pains: [
    {
      icon: "ri-question-line",
      title: "You know AI matters, but not where to start",
      text: "Dozens of possible use cases, zero clarity on which one pays first. The assessment ranks them by impact and effort.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "You're afraid of wasting money on hype",
      text: "Every vendor promises transformation. We show you projected ROI per use case before you spend a dollar.",
    },
    {
      icon: "ri-team-line",
      title: "Your team is skeptical (with good reason)",
      text: "Failed software rollouts make teams wary. A concrete, small-first roadmap earns buy-in instead of demanding it.",
    },
  ],
  servicesTitle: "What you get",
  servicesIntro: "A practical, written deliverable — not a slide deck of buzzwords.",
  services: [
    {
      icon: "ri-file-list-3-line",
      title: "Prioritized use-case report",
      text: "Your 3 highest-leverage AI opportunities, ranked by ROI potential and implementation effort.",
    },
    {
      icon: "ri-calculator-line",
      title: "ROI & cost estimate per use case",
      text: "Honest numbers: what each automation would save or earn you, and what it would cost to build.",
    },
    {
      icon: "ri-road-map-line",
      title: "90-day roadmap",
      text: "A sequenced plan: what to automate first, what comes next, and what to skip for now.",
    },
    {
      icon: "ri-user-voice-line",
      title: "30-min session with an AI strategist",
      text: "A working session with someone who has deployed these systems — not a sales rep reading a script.",
    },
    {
      icon: "ri-shield-check-line",
      title: "Risk & readiness check",
      text: "Data readiness, integration complexity and change-management risks flagged up front.",
    },
    {
      icon: "ri-gift-line",
      title: "Yours to keep",
      text: "The report is yours whether we work together or not. Use it with any vendor — or build it yourself.",
    },
  ],
  bandTitle: "How it works",
  bandText: "Three simple steps. Less than a week from booking to report.",
  bandPoints: [
    "1. Book — pick a 30-minute slot on our calendar. NDA available on request.",
    "2. Working session — we map your workflows live and spot the leverage points together.",
    "3. Report in 24 hours — your prioritized use cases, ROI estimates and 90-day roadmap, in writing.",
  ],
  processTitle: "What happens after the assessment",
  processIntro: "If you decide to move forward, this is the path from report to results.",
  steps: SHARED_STEPS,
  stats: SHARED_STATS,
  faqTitle: "Frequently asked questions",
  faqs,
  relatedTitle: "Explore more",
  related: [
    {
      href: "/ai-automation-agency-houston/",
      label: "AI Automation Agency in Houston",
      desc: "Local AI automation partner for Texas SMBs.",
    },
    {
      href: "/industries/ai-agents-for-law-firms/",
      label: "AI Agents for Law Firms",
      desc: "24/7 intake, triage and automation for firms.",
    },
    {
      href: "/industries/ai-automation-for-logistics/",
      label: "AI Automation for Logistics",
      desc: "Cut back-office costs for 3PLs and distributors.",
    },
  ],
  ctaTitle: "Get your AI opportunity report",
  ctaText:
    "Book your free 30-minute session now. Within 24 hours you'll hold a written report with your 3 highest-ROI opportunities and a 90-day roadmap.",
};
